import React, { Component } from 'react';
import styled from 'styled-components';
import { get, keyBy as keyby, isEqual, map } from 'lodash';
import {
  DATA_TRACK_TYPE_INTERNAL,
  MY_ZONE_OFFERS_PAGE,
  MY_ZONE_SHOPS_PAGE,
  OFFERS,
  SHOPS,
} from '../../../../lib/constants';
import { trackAnalyticsNew } from '../../../../lib/utils';
import MaxWidthLayout, { MaxWidthLayoutMobile } from '../../../templates/MaxWidthLayout';
import MediaQueryHelper from '../../../templates/MediaQueryHelper';
import MyZoneContentWrapper from './MyZoneContentWrapper';
import MyZoneShopCard from './MyZoneShopCard';
import RenderLayoutViews from '../shared/RenderLayoutViews';
import TopBottomPadding from '../shared/TopBottomPadding';
import OfferCard from '../Offer/OfferCard';
import TabGroup from '../shared/TabGroup';

const TABS = [MY_ZONE_SHOPS_PAGE, MY_ZONE_OFFERS_PAGE];
const TAB_ANALYTICS_NAMES = {
  [MY_ZONE_SHOPS_PAGE]: 'favourite-stores',
  [MY_ZONE_OFFERS_PAGE]: 'saved-offers',
};

const ScreenReaderOnlyH1 = styled.h1`
  position:absolute;
  left:-10000px;
  top:auto;
  width:1px;
  height:1px;
  overflow:hidden;
`;

class MyZonePage extends Component {
  constructor(props) {
    super(props);
    const activeSlug = props.urlData && props.urlData.query && props.urlData.query.show;

    this.state = {
      activeTab: TABS.includes(activeSlug) ? activeSlug : MY_ZONE_SHOPS_PAGE,
    };

    const {
      myZonePageData = [],
    } = this.props;

    // convert array of data into a dictionary where the keys are the slugs
    this.myZoneData = keyby(myZonePageData, 'slug');

    // slice() make a copy of the data
    // a copy is made because my zone needs to show the initial shop/offer
    //    cards even if the user unfavourites it while on the page
    this.initialShopsData = this.props.userShopsData.slice();
    this.initialOffersData = this.props.userOffersData.slice();

    this.setActiveTab = this.setActiveTab.bind(this);
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (!isEqual(get(nextProps, 'userData'), get(this.props, 'userData')) ||
        !isEqual(this.state, nextState)) {
      return true;
    }
    return nextProps.children !== this.props.children;
  }

  setActiveTab(activeSlug) {
    if (activeSlug === this.state.activeTab) {
      return;
    }
    this.setState({ activeTab: activeSlug });
  }

  render() {
    const {
      locale,
      userData,
      pageStateModifiers,
      globalPageConfig,
    } = this.props;

    const tabsToRender = TABS.map((tab) => {
      const tabData = this.myZoneData[tab];
      if (!tabData) {
        return null;
      }

      return {
        id: tabData.slug || '',
        label: tabData.displayName || '',
        analytics: {
          ...trackAnalyticsNew({
            location: 'page:header',
            name: TAB_ANALYTICS_NAMES[tab],
          }),
          dataTrackType: DATA_TRACK_TYPE_INTERNAL,
        },
      };
    }).filter(Boolean);

    const Tabs = ({ mobile }) => {
      if (mobile) {
        return (
          <MaxWidthLayoutMobile>
            <TabGroup
              tabs={tabsToRender}
              activeTab={this.state.activeTab}
              onTabChange={this.setActiveTab}
              isMobile
              variant="myZone"
              ariaLabel="My Zone tabs"
              idPrefix="my-zone-tabs-mobile"
              focusActiveTabOnChange
            />
          </MaxWidthLayoutMobile>);
      }
      return (
        <TabGroup
          tabs={tabsToRender}
          activeTab={this.state.activeTab}
          onTabChange={this.setActiveTab}
          variant="myZone"
          ariaLabel="My Zone tabs"
          idPrefix="my-zone-tabs"
          focusActiveTabOnChange
        />
      );
    };

    const activeTabData = this.myZoneData[this.state.activeTab];
    if (!activeTabData) {
      return null;
    }

    const PageToRender = ({ mobile }) => {
      if (this.state.activeTab === MY_ZONE_SHOPS_PAGE) {
        const favShopIds = map(userData.shops, 'id') || [];
        return (
          <MyZoneContentWrapper
            globalPageConfig={globalPageConfig}
            itemsData={this.initialShopsData}
            favItemIds={favShopIds}
            itemType={SHOPS}
            CompToUse={MyZoneShopCard}
            pageStateModifiers={pageStateModifiers}
            locale={locale}
            tabData={activeTabData}
            userData={userData}
            isMobile={mobile}
          />);
      } else if (this.state.activeTab === MY_ZONE_OFFERS_PAGE) {
        const favOffersIds = map(userData.offers, 'id') || [];
        return (
          <MyZoneContentWrapper
            globalPageConfig={globalPageConfig}
            itemsData={this.initialOffersData}
            favItemIds={favOffersIds}
            itemType={OFFERS}
            CompToUse={OfferCard}
            pageStateModifiers={pageStateModifiers}
            locale={locale}
            tabData={activeTabData}
            userData={userData}
            isMobile={mobile}
          />);
      }
      return null;
    };

    const { myZoneHeadingLabel = 'My Zone' } = globalPageConfig;

    const desktopPage = (
      <div>
        <ScreenReaderOnlyH1>{myZoneHeadingLabel}</ScreenReaderOnlyH1>
        <Tabs />
        <PageToRender />
      </div>
    );

    const mobilePage = (
      <div>
        <ScreenReaderOnlyH1>{myZoneHeadingLabel}</ScreenReaderOnlyH1>
        <Tabs mobile />
        <PageToRender mobile />
      </div>
    );

    const renderComp = (
      <TopBottomPadding>
        <MaxWidthLayout>
          <MediaQueryHelper
            desktopChildren={desktopPage}
            mobileChildren={mobilePage}
          />
        </MaxWidthLayout>
        <RenderLayoutViews
          views={activeTabData.layout ? activeTabData.layout : []}
          locale={locale}
          userData={userData}
          pageStateModifiers={pageStateModifiers}
        />
      </TopBottomPadding>
    );

    return renderComp;
  }
}

export default MyZonePage;
